import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/about', label: 'About' },
    { path: '/blog', label: 'Blog' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${ 
        isScrolled 
          ? 'bg-[#0A1420]/90 backdrop-blur-xl border-b border-[#5D676E]/25 shadow-sm' 
          : 'bg-[#0A1420]/60 backdrop-blur-sm'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-24">
          <Link to="/" className="flex items-center group" data-testid="logo-link">
            <img
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_php-sample-build/artifacts/ftajnjjx_New%20Noax%20Logo%20-%2003.png"
              alt="Noax Media logo — real-time US lead generation for Medicare and final expense insurance"
              className="h-16 lg:h-20 w-auto"
              data-testid="logo-image"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-10">
            {navLinks.map((link) => {
              const isActive = link.path === '/'
                ? location.pathname === '/'
                : location.pathname === link.path || location.pathname.startsWith(link.path + '/');
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative text-sm font-medium transition-colors group ${
                    isActive ? 'text-[#31638C]' : 'text-gray-300 hover:text-[#31638C]'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#31638C] transition-all duration-300 ${
                    isActive ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>
              );
            })}
            <Link
              to="/about#contact"
              className="px-6 py-2.5 bg-gradient-to-r from-[#31638C] to-[#31638C] text-white text-sm font-medium rounded-full hover:-translate-y-0.5 transition-all duration-300 hover:shadow-lg hover:shadow-[#31638C]/40"
              data-testid="cta-button-nav"
            >
              Get Started
            </Link>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-white"
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-b border-[#5D676E]/25"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block text-base font-medium transition-colors ${
                    location.pathname === link.path ? 'text-[#31638C]' : 'text-gray-300 hover:text-[#31638C]'
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/about#contact"
                className="block w-full text-center px-6 py-2.5 bg-gradient-to-r from-[#31638C] to-[#31638C] text-white text-sm font-medium rounded-full"
                data-testid="cta-button-mobile"
              >
                Get Started
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
